import { useI18n } from '@ecom/i18n';
import { Card, EmptyState } from '@ecom/ui-kit';
import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { TopProduct } from '@/features/analytics/types';
import { fetchProductImageObjectUrl, useProductImagesQuery } from '@/features/products/api';
import { formatCatalogPrice } from '@/features/products/format-price';
import { useSession } from '@/lib/session';

/**
 * Resolves a product's FIRST real uploaded image (Images Produits lot) to
 * a displayable blob URL — same pattern `CartItemsList.tsx` (storefront)
 * already established: `<img src>` cannot carry the `Authorization`
 * header this route requires, so the bytes are fetched here and handed
 * back as a local object URL, revoked on unmount/replacement. A product
 * with no uploaded image falls back to the same honest letter-tile every
 * other card in this app already uses — never a fabricated photo.
 */
function TopProductThumbnail({
  productId,
  productName,
}: {
  productId: string;
  productName: string;
}): ReactElement {
  const { accessToken, activeShopId } = useSession();
  const imagesQuery = useProductImagesQuery(productId);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const firstImageId = imagesQuery.data?.[0]?.id;

  useEffect(() => {
    if (firstImageId === undefined || accessToken === null || activeShopId === null) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    void fetchProductImageObjectUrl(productId, firstImageId, accessToken, activeShopId).then(
      (url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setObjectUrl(url);
      },
      () => {
        // No usable image — the letter-tile fallback below covers it.
      },
    );
    return () => {
      cancelled = true;
      if (createdUrl !== null) URL.revokeObjectURL(createdUrl);
    };
  }, [productId, firstImageId, accessToken, activeShopId]);

  return (
    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-surface2">
      {objectUrl !== null ? (
        <img src={objectUrl} alt={productName} className="h-full w-full object-cover" />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center text-inkFaint"
          aria-hidden="true"
        >
          <span className="font-display text-sm">{productName.charAt(0).toUpperCase()}</span>
        </div>
      )}
    </div>
  );
}

/**
 * "Bloc Top Produits" — Sprint UX/UI Merchant Dashboard V2: photo réelle,
 * nom, ventes, montant. Data is `MerchantAnalyticsOverview.topProducts`
 * (top 5 by quantity sold, CANCELLED excluded) — already fetched by the
 * caller via `useMerchantAnalyticsQuery()`, passed in rather than
 * re-fetched here.
 */
export function TopProducts({ products }: { products: readonly TopProduct[] }): ReactElement {
  const { t } = useI18n();
  return (
    <Card className="p-5">
      <h3 className="font-display text-sm font-bold text-ink">
        {t('merchant.dashboard.topProducts.title')}
      </h3>
      {products.length === 0 ? (
        <div className="mt-3">
          <EmptyState
            title={t('merchant.dashboard.topProducts.emptyTitle')}
            description={t('merchant.dashboard.topProducts.emptyDescription')}
          />
        </div>
      ) : (
        <ul className="mt-3 flex flex-col gap-3">
          {products.map((product) => (
            <li key={product.productId}>
              <Link
                to={`/products/${product.productId}`}
                className="flex items-center gap-3 rounded-lg px-1 py-1 transition-colors hover:bg-surface2"
              >
                <TopProductThumbnail
                  productId={product.productId}
                  productName={product.productName}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">
                    {product.productName}
                  </span>
                  <span className="block text-xs text-inkFaint">
                    {t('merchant.dashboard.topProducts.soldCount', {
                      count: product.quantitySold,
                      plural: product.quantitySold > 1 ? 's' : '',
                    })}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-sm font-semibold text-ink">
                  {formatCatalogPrice(product.revenue)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
